import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  FolderKanban,
  Clock,
  AlertTriangle,
  Calendar,
  Hourglass,
  Users,
  Building2,
  ListTodo,
} from "lucide-react";
import { getDashboard } from "../services/dashboard";
import { useAuth } from "../hooks/useAuth";
import { listarTiposProjeto } from "../services/tiposProjeto";
import { TipoProjetoChip } from "../components/TipoProjetoChip";
import { NotasPessoaisCard } from "../components/NotasPessoaisCard";

export function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: getDashboard });
  const { data: tiposProjeto = [] } = useQuery({ queryKey: ["tipos-projeto", "ativos"], queryFn: () => listarTiposProjeto() });

  if (isLoading || !data) return <p className="text-sm text-slate-400">Carregando…</p>;

  const ehGestor = data.perfil === "gestor" || data.perfil === "admin";
  const ehAdmin = data.perfil === "admin";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Olá, {user?.nome.split(" ")[0]} 👋</h1>
        <p className="text-sm text-slate-500 mt-0.5">Aqui está um resumo do seu dia.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Cada card leva pra /tarefas já filtrado (jul/2026): `proprias=1` deixa
            só as atribuídas a mim (espelha "minhas" do backend), e o filtro
            específico casa com a contagem do card. */}
        <Stat icon={ListTodo} label="Minhas tarefas" value={data.minhasTarefasAFazer} color="bg-blue-500" link="/tarefas?proprias=1" />
        <Stat icon={AlertTriangle} label="Vencidas" value={data.minhasVencidas} color="bg-red-500" link="/tarefas?proprias=1&atrasadas=1" />
        <Stat icon={Clock} label="Vencem em 24h" value={data.minhasVencendo24h} color="bg-amber-500" link="/tarefas?proprias=1&vence24h=1" />
      </div>

      {/* Próximas tarefas + Anotações pessoais lado a lado em desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
            <Calendar size={16} className="text-blue-600" /> Suas próximas tarefas
          </h2>
          {data.proximasTarefas.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Sem tarefas pendentes 🎉</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.proximasTarefas.map((t) => {
                const venc = new Date(t.prazo);
                const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
                const dias = Math.ceil((venc.getTime() - hoje.getTime()) / (24 * 60 * 60 * 1000));
                const original = t.prazoOriginal ? new Date(t.prazoOriginal) : venc;
                const atrasada = new Date() > original;
                return (
                  <li key={t.id} className="py-2 flex items-center gap-3">
                    <Link to={`/tarefas?tarefa=${t.id}`} className="flex-1 min-w-0 hover:text-blue-600">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{t.titulo}</p>
                      {t.projeto && (
                        <div className="mt-0.5 max-w-full">
                          <TipoProjetoChip
                            tipo={tiposProjeto.find((tp) => tp.slug === t.projeto?.tipo)}
                            nome={t.projeto.nome}
                            fotoUrl={(t.projeto as any).fotoUrl ?? null}
                            tamanho="xs"
                          />
                        </div>
                      )}
                    </Link>
                    <span className={`text-[11px] font-medium shrink-0 ${atrasada ? "text-red-600" : dias <= 1 ? "text-amber-600" : "text-slate-500"}`}>
                      {atrasada ? `${Math.abs(dias)}d atrasada` : dias === 0 ? "vence hoje" : `${dias}d`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <NotasPessoaisCard />
      </div>

      {ehGestor && data.gestor && (
        <>
          <h2 className="text-sm font-semibold text-slate-600 uppercase">Gestão</h2>
          {/* Cada card leva pra lista já filtrada (jul/2026), batendo com a
              contagem. Projetos: ?situacao=; tarefas equipe: escopo todas +
              atrasadas. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={FolderKanban} label="Projetos ativos" value={data.gestor.projetosAtivos} color="bg-blue-500" link="/projetos?situacao=ativos" />
            <Stat icon={AlertTriangle} label="Projetos atrasados" value={data.gestor.projetosAtrasados} color="bg-red-500" link="/projetos?situacao=atrasados" />
            {/* "Aguardando cliente" agora conta e abre TAREFAS na coluna
                Aguardando cliente (pedido do Ricardo, jul/2026), não projetos. */}
            <Stat icon={Hourglass} label="Aguardando cliente" value={data.gestor.tarefasAguardandoCliente} color="bg-purple-500" link="/tarefas?escopo=todas&status=aguardando_cliente" />
            <Stat icon={ListTodo} label="Tarefas equipe vencidas" value={data.gestor.tarefasEquipeVencidas} color="bg-orange-500" link="/tarefas?escopo=todas&atrasadas=1" />
          </div>
        </>
      )}

      {ehAdmin && data.admin && (
        <>
          <h2 className="text-sm font-semibold text-slate-600 uppercase">Administração</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat icon={Users} label="Usuários ativos" value={data.admin.usuariosAtivos} color="bg-purple-500" link="/usuarios" />
            <Stat icon={Building2} label="Clientes ativos" value={data.admin.clientesAtivos} color="bg-blue-500" link="/clientes" />
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, link }: { icon: any; label: string; value: number | string; color: string; link?: string }) {
  const inner = (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow h-full">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${color} text-white flex items-center justify-center shrink-0`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold text-slate-800 truncate">{value}</p>
          <p className="text-[11px] text-slate-500 leading-tight">{label}</p>
        </div>
      </div>
    </div>
  );
  if (link) return <Link to={link} className="block">{inner}</Link>;
  return inner;
}
